import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, inject, Injector, signal, viewChild } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { CommonButtonRowWidget, CommonForm, CommonMultiSelectField, CommonMultiselectFieldWidget, CommonOptionsFieldConfig, CommonSelectField, CommonSelectFieldWidget, CommonTextField, CommonTextFieldWidget } from "@eforall/common";
import { DashboardDescriptiveSelectFieldWidget, DescriptiveSelectItem } from "@eforall/dashboard";
import { VolunteerInterestRoles, VolunteerInterestRolesName, VolunteerStatusForm, VolunteerStatusId } from "@interfaces";
import { DomainDataService } from "../../../../../../services";
import { VolunteerDialogDataService } from "../../volunteer-dialog-data.service";

interface FormFields {
	attendedOrientation?: boolean,
	statusId: number,
	languages?: string[],
	roles?: string[],
	topics?: number[],
	notes?: string,
}

@Component({
	selector: 'edit-volunteer-status-dialog-part',
	standalone: true,
	imports: [
		CommonButtonRowWidget,
		CommonMultiselectFieldWidget,
		CommonSelectFieldWidget,
		CommonTextFieldWidget,
		DashboardDescriptiveSelectFieldWidget
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './edit-volunteer-status-dialog.part.html',
})

export class EditVolunteerStatusDialogPart implements AfterViewInit {

	public statusSaveBtn = viewChild.required<ElementRef<HTMLButtonElement>>('statusSaveBtn');

	private readonly injector = inject(Injector)
	private readonly domainData = inject(DomainDataService);
	public readonly dialogDataService = inject(VolunteerDialogDataService);
	private readonly form = new CommonForm<FormFields>(this.injector);


	public currentValues = computed(() => {
		const volunteerDetails = this.dialogDataService.volunteerDetails();
		if (!volunteerDetails) return undefined;

		const languages: string[] = [];
		if (volunteerDetails.canVolunteerInEnglish) {
			languages.push('EN');
		}
		if (volunteerDetails.canVolunteerInSpanish) {
			languages.push('ES');
		}

		const roles: string[] = [];
		if (volunteerDetails.isMentorApproved) {
			roles.push(VolunteerInterestRoles.Mentor);
		}
		if (volunteerDetails.isInClassApproved) {
			roles.push(VolunteerInterestRoles.InClassSpecialist);
		}
		if (volunteerDetails.isOfficeHoursApproved) {
			roles.push(VolunteerInterestRoles.OfficeHoursSpecialist);
		}

		const topics: number[] = volunteerDetails.volunteerTopics
			.filter(topic => topic.isApproved)
			.map(topic => topic.topicId)
			.filter(topicId => {
				const topic = this.domainData.data().topicsMap[topicId];
				return topic && topic.enabled && topic.needsSpecialist;
			}) ?? [];


		return {
			statusId: volunteerDetails.volunteerStatusId,
			attendedOrientation: volunteerDetails.attendedOrientation,
			languages,
			roles,
			topics
		};
	});

	constructor() {
		// Subscribe to volunteer details changes to update status options
		toObservable(this.dialogDataService.volunteerDetails).subscribe((details) => {
			if (details) {
				this.updateStatusConfig(details.volunteerStatusId);
			}
		})
	}

	ngAfterViewInit() {
		this.form.setSubmitButton(
			this.statusSaveBtn(),
			async (values: FormFields, changed: string[]) => {
				// added this check here to avoid race condition of fetching permissions before app data is loaded
				if (!this.dialogDataService.hasVolunteerEditPermissions()) return;
				await this.onSaveVolunteerStatus(values);
			}
		);
	}

	private async onSaveVolunteerStatus(values: FormFields) {

		const volunteerDetails = this.dialogDataService.volunteerDetails();

		if (!volunteerDetails) return;

		let payload: VolunteerStatusForm = {
			volunteerId: volunteerDetails.volunteerId,
			fromVolunteerStatusId: volunteerDetails.volunteerStatusId,
			toVolunteerStatusId: values.statusId,
			notes: values.notes
		};

		// if the status is approved or approved with caution, include additional fields
		if (values.statusId === VolunteerStatusId.Approved || values.statusId === VolunteerStatusId.ApprovedWithCaution) {
			payload = {
				...payload,
				languages: values.languages,
				roles: values.roles,
				topics: values.topics,
				attendedOrientation: values.attendedOrientation,
			}
		}

		await this.dialogDataService.volunteerStatusUpdate(payload);
		// Reset the form after saving
		this.form.resetAll();
	}


	revertChanges() {
		if (!this.dialogDataService.hasVolunteerEditPermissions()) return;
		this.form.resetAll();
	}

	private isRemovedStatus(statusId: number): boolean {
		return [
			VolunteerStatusId.RemovedDueToInactivity,
			VolunteerStatusId.RemovedDueToCause,
			VolunteerStatusId.RemovedByRequest
		].includes(statusId);
	}

	private isApprovedStatus(statusId: number): boolean {
		return [
			VolunteerStatusId.ApprovedWithCaution,
			VolunteerStatusId.Approved
		].includes(statusId);
	}

	private getDisabledVolunteerStatus(currentStatusId: number, statusId: number): boolean {

		// If the current status is the same as the status being checked, it should not be disabled.
		if (currentStatusId === statusId) return false;

		switch (currentStatusId) {

			//If current status is Pending -> allow transition to Approved, Approved With Caution, Rejected or RemovedDueToInactivity
			case VolunteerStatusId.Pending:
				return !this.isApprovedStatus(statusId)
					&& statusId !== VolunteerStatusId.Rejected
					&& statusId !== VolunteerStatusId.RemovedDueToInactivity;

			//If current status is Rejected/RemovedDueToInactivity -> allow transition to Approved, Approved With Caution, or Pending
			case VolunteerStatusId.Rejected:
			case VolunteerStatusId.RemovedDueToInactivity:
				return !this.isApprovedStatus(statusId) && statusId !== VolunteerStatusId.Pending;

			// If current status is approved status -> allow transition to any removal statuses
			case VolunteerStatusId.Approved:
			case VolunteerStatusId.ApprovedWithCaution:
				return !this.isRemovedStatus(statusId) && !this.isApprovedStatus(statusId);


			// If current status is a removal status -> allow transition to approved statuses
			case VolunteerStatusId.RemovedDueToCause:
			case VolunteerStatusId.RemovedByRequest:
				return !this.isApprovedStatus(statusId);

			default:
				return false;
		}
	}


	// Stable identity: `_statusConfigSignal` is created once, and `status` returns the same signal each time.
	// Consumers keep their subscriptions; only the signal’s value changes, so reactivity stays intact.
	private readonly _statusConfigSignal = signal<CommonOptionsFieldConfig<number>>({
		label: 'Status',
		options: [],
		isRequired: true,
		isSpanish: false,
		isEnabled: this.dialogDataService.hasVolunteerEditPermissions,
	});

	private updateStatusConfig(currentStatusId: number) {
		const domainData = this.domainData.data();
		const options = Object.values(domainData.volunteerStatusesMap)
			.map(status => ({
				text: status.name,
				subText: status.desc,
				value: status.volunteerStatusId,
				disabled: this.getDisabledVolunteerStatus(currentStatusId, status.volunteerStatusId),
			}));
		this._statusConfigSignal.update(current => ({
			...current,
			options
		}));
	};

	readonly status = computed<CommonSelectField<number>>(() => ({
		config: this._statusConfigSignal,
		actualValue: computed(() => this.currentValues()?.statusId || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'statusId',
		form: this.form,
	}));

	/**
	 * Get the appropriate FontAwesome icon class based on volunteer status ID
	 */
	private getStatusIcon(statusId: number): string {
		if (statusId == VolunteerStatusId.Pending) return 'fa-fw fa-circle-info fa-solid text-secondary';
		else if (statusId == VolunteerStatusId.Rejected) return 'fa-fw fa-circle-xmark fa-solid text-danger';
		else if (statusId == VolunteerStatusId.Approved) return 'fa-fw fa-circle-check fa-solid text-success';
		else if (statusId == VolunteerStatusId.ApprovedWithCaution) return 'fa-fw fa-circle-check fa-solid text-warning';
		else if (statusId == VolunteerStatusId.RemovedDueToInactivity || statusId == VolunteerStatusId.RemovedDueToCause || statusId == VolunteerStatusId.RemovedByRequest) return 'fa-fw fa-circle-minus fa-solid text-danger';
		else return 'fa-fw fa-circle-info fa-solid text-info';
	}

	/**
	 * Extract color from icon class text-* utility classes
	 */
	private getIconColor(iconClass: string): string {
		if (iconClass.includes('text-secondary')) return '#6c757d';
		else if (iconClass.includes('text-danger')) return '#dc3545';
		else if (iconClass.includes('text-success')) return '#28a745';
		else if (iconClass.includes('text-warning')) return '#ffc107';
		else if (iconClass.includes('text-info')) return '#17a2b8';
		else return '#4CAF50'; // default green
	}

	/**
	 * Convert status options to DescriptiveSelectItem format for the dashboard widget
	 */
	public readonly descriptiveStatusItems = computed<DescriptiveSelectItem[]>(() => {
		const config = this._statusConfigSignal();
		const options = config.options;

		return options.map(option => {
			const iconClass = this.getStatusIcon(option.value);
			const iconColor = this.getIconColor(iconClass);

			return {
				uniqueId: option.value,
				text: option.text,
				subText: option.subText,
				disabled: option.disabled || false,
				iconClass: iconClass,
				iconColor: option.disabled ? '#999' : iconColor
			};
		});
	});

	/**
	 * Get the selected descriptive item for the dashboard widget
	 */
	public readonly selectedDescriptiveItem = computed<DescriptiveSelectItem | undefined>(() => {
		const currentValue = this.status().pendingValue() || this.status().actualValue();
		return this.descriptiveStatusItems().find(item => item.uniqueId === currentValue);
	});

	/**
	 * Check if the descriptive select field is enabled
	 */
	public readonly isDescriptiveSelectEnabled = computed(() => {
		const isEnabled = this._statusConfigSignal().isEnabled;
		return typeof isEnabled === 'function' ? isEnabled() : isEnabled;
	});

	/**
	 * Get the custom icon for the current selected status
	 */
	public readonly currentStatusIcon = computed<string>(() => {
		const selectedItem = this.selectedDescriptiveItem();
		return selectedItem?.iconClass || '';
	});

	/**
	 * Handle selection change from the descriptive select field
	 */
	public onDescriptiveSelectionChange(item: DescriptiveSelectItem | undefined) {
		if (item?.uniqueId !== undefined && typeof item.uniqueId === 'number') {
			// Update the pending value - this will sync with the hidden common-select-field
			this.status().pendingValue.set(item.uniqueId);
		}
	}

	// to exclude additional fields(attendedOrientation,language, roles and topics) fields based on the status selected(Exclude for pending and rejected)
	private excludeAdditionalFields = computed(() => {
		const statusField = this.status();
		const pending = statusField.pendingValue();
		const actual = statusField.actualValue();
		const statusId = pending ? pending : actual;

		return statusId === VolunteerStatusId.Pending || statusId === VolunteerStatusId.Rejected;
	});

	// additional fields are enabled only for approved status
	private readonly areAdditionalFieldsEnabled = computed(() => {
		if (!this.dialogDataService.hasVolunteerEditPermissions()) return false;
		const statusField = this.status();
		const statusId = statusField.pendingValue() ?? statusField.actualValue();

		return this.isApprovedStatus(statusId);
	});


	// Attended Orientation droplist options
	private readonly attendedOrientationOpts = [
		{ value: false, text: 'No' },
		{ value: true, text: 'Yes' }
	];
	readonly attendedOrientation = computed<CommonSelectField<boolean | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<boolean>>({
			label: 'Has Attended an Orientation',
			options: this.attendedOrientationOpts,
			isRequired: true,
			isSpanish: false,
			isEnabled: this.areAdditionalFieldsEnabled,
		}),
		formFieldName: 'attendedOrientation',
		form: this.form,
		isExcluded: this.excludeAdditionalFields,
		actualValue: computed(() => this.currentValues()?.attendedOrientation),
		pendingValue: signal<boolean | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	private readonly languagesOpts = computed(() => [
		{ value: 'EN', text: 'English' },
		{ value: 'ES', text: 'Spanish' },
	]);
	readonly languages = computed<CommonMultiSelectField<string>>(() => ({
		config: signal<CommonOptionsFieldConfig<string>>({
			label: "Languages",
			isRequired: true,
			isSpanish: false,
			options: this.languagesOpts(),
			isEnabled: this.areAdditionalFieldsEnabled,
		}),
		formFieldName: 'languages',
		form: this.form,
		isExcluded: this.excludeAdditionalFields,
		actualValue: computed(() => this.currentValues()?.languages || []),
		pendingValue: signal<string[] | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	private readonly rolesOpts = computed(() => [
		{ value: VolunteerInterestRoles.Mentor, text: VolunteerInterestRolesName.IsMentorApproved },
		{ value: VolunteerInterestRoles.InClassSpecialist, text: VolunteerInterestRolesName.IsInClassApproved },
		{ value: VolunteerInterestRoles.OfficeHoursSpecialist, text: VolunteerInterestRolesName.IsOfficeHoursApproved },
	]);
	readonly roles = computed<CommonMultiSelectField<string>>(() => ({
		config: signal<CommonOptionsFieldConfig<string>>({
			label: "Roles",
			isRequired: true,
			isSpanish: false,
			options: this.rolesOpts(),
			isEnabled: this.areAdditionalFieldsEnabled,
		}),
		formFieldName: 'roles',
		form: this.form,
		isExcluded: this.excludeAdditionalFields,
		actualValue: computed(() => this.currentValues()?.roles || []),
		pendingValue: signal<string[] | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	private readonly topicsOpts = computed(() =>
		this.domainData.data().topics.filter(t => t.enabled && t.needsSpecialist).map((item) => ({
			value: item.topicId,
			text: item.name
		}))
	);
	readonly topics = computed<CommonMultiSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: "Topics",
			isRequired: true,
			isSpanish: false,
			options: this.topicsOpts(),
			isEnabled: this.areAdditionalFieldsEnabled,
		}),
		formFieldName: 'topics',
		form: this.form,
		isExcluded: this.isTopicsExcluded,
		actualValue: computed(() => this.currentValues()?.topics || []),
		pendingValue: signal<number[] | null>(null),
		saving: signal(false),
		error: signal(''),
	}));

	private isTopicsExcluded = computed(() => {
		const excludeAdditionaFields = this.excludeAdditionalFields();
		if (excludeAdditionaFields) return true;

		const roles = this.roles();
		const role_values = roles.pendingValue() ?? roles.actualValue();

		return ![
			VolunteerInterestRoles.InClassSpecialist,
			VolunteerInterestRoles.OfficeHoursSpecialist
		].some(role => role_values.includes(role));
	});

	readonly notes = computed<CommonTextField>(() => ({
		config: signal({
			label: 'Notes',
			min: 0,
			max: 2000,
			fixCase: 'sentence-case' as const,
			isRequired: false,
			multiLine: true,
			isSpanish: false,
			isEnabled: this.dialogDataService.hasVolunteerEditPermissions,
		}),
		actualValue: computed(() => ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'notes',
		form: this.form,
	}));
}